import { useState } from "react";
import { useSelector } from "react-redux";
import { Routes, Route } from "react-router-dom";
import SurveyTabs from "./SurveyTabs/SurveyTabs";
import TakeASurvey from "./TakeSurvey/TakeASurvey";
import TakeSharedSurvey from "./TakeSharedSurvey/TakeSharedSurvey";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import {
  useListQuestionnairesForAccountQuery,
  useListSurveysForAccountQuery,
} from "@generated";
import { useAuthorization } from "../../common/providers/AuthorizationProvider";
import { RootState } from "@reducers/rootReducer";
import CreateProjectFromRecommendations from "./CreateProjectFromRecommendations/CreateProjectFromRecommendations";
import API from "../../utils/API/API";
import { useEffect } from "react";
import {
  V1SurveyResource,
  ListSurveysForAccountApiResponse,
  V1ServiceResource,
} from "@generated";

function SurveyMspa({ account_slug, account_id }) {
  const authorized = useAuthorization();
  const accessToken = useSelector((state: RootState) => state.auth.token);
  const apiHost = process.env.REACT_APP_SCOPESTACK_API_HOST;
  const { accountSlug } = useSelector((state: RootState) => state.slug);
  const [surveys, setSurveys] =
    useState<null | ListSurveysForAccountApiResponse>(null);
  const [createProjectData, setCreateProjectData] = useState<null | any>(null);
  const [servicesFromApi, setServicesFromApi] =
    useState<null | V1ServiceResource[]>(null);
  const [includedFromServices, setIncludedFromServices] =
    useState<null | any[]>(null);

  useEffect(() => {
    if (accessToken !== "") {
      API.Get(
        `${apiHost}/${accountSlug}/v1/surveys?sort=-updated-at&include=project,questionnaire,survey-recommendations,survey-responses&page[size]=1000`,
        accessToken
      ).then((response) => {
        let surveys = response.data.data;
        let meta = response.data.meta;
        let included = response.data.included;
        const getNextPage = (nextLink, surveysPage) => {
          API.Get(nextLink, accessToken).then((res) => {
            surveys = surveys.concat(res.data.data);
            included = included.concat(res.data.included);

            if (res.data.links.next) {
              getNextPage(res.data.links.next, surveys);
            } else if (
              !res.data.links.next &&
              res.data.meta["record-count"] < surveys.length
            ) {
              getNextPage(res.data.links.last, surveys);
            } else {
              setSurveys({
                data: surveys,
                included,
                meta,
              });
            }
          });
        };

        if (meta["page-count"] > 1) {
          getNextPage(response.data.links.next, surveys);
        } else {
          setSurveys({
            data: surveys,
            included,
            meta,
          });
        }
      });

      const url = `${apiHost}/${accountSlug}/v1/services?include=subservices,resource,phase,subservices.resource,service-category&page[size]=500&filter[survey_recommended]=true`;
      API.Get(url, accessToken).then((response) => {
        let services = response.data.data;
        let meta = response.data.meta;
        let included = response.data.included;
        const getNextPage = (nextLink) => {
          API.Get(nextLink, accessToken).then((res) => {
            services = services.concat(res.data.data);
            included = included.concat(res.data.included);

            if (res.data.links.next) {
              getNextPage(res.data.links.next);
            } else if (
              !res.data.links.next &&
              res.data.meta["record-count"] < services.length
            ) {
              getNextPage(res.data.links.last);
            } else {
              setServicesFromApi(services);
              setIncludedFromServices(included);
            }
          });
        };

        if (meta["page-count"] > 1) {
          getNextPage(response.data.links.next);
        } else {
          setServicesFromApi(services);
          setIncludedFromServices(included);
        }
      });
    }
  }, [accessToken]);

  const {
    data: questionnaires,
    isLoading: questionnairesLoading,
    refetch,
  } = useListQuestionnairesForAccountQuery({
    slug: account_slug,
    include: ["questions"],
  });

  const isLoading =
    !authorized ||
    !surveys ||
    questionnairesLoading ||
    !servicesFromApi ||
    !includedFromServices;

  return (
    <Routes>
      <Route
        path="/"
        element={
          <SurveyTabs
            surveys={surveys}
            surveysLoading={isLoading}
            accessToken={accessToken}
            questionnaires={questionnaires}
            refetch={refetch}
          />
        }
      />
      <Route
        path="/:questionnaireId/take"
        element={
          <TakeASurvey
            accessToken={accessToken}
            surveys={surveys}
            setCreateProjectData={(data) => setCreateProjectData(data)}
            servicesFromApi={servicesFromApi}
            includedFromServices={includedFromServices}
          />
        }
      />
      <Route
        path="/:surveyId/create_project"
        element={
          <CreateProjectFromRecommendations
            accessToken={accessToken}
            surveys={surveys}
            createProjectData={createProjectData}
          />
        }
      />
    </Routes>
  );
}

export default SurveyMspa;
